import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FileSignature, Download, RefreshCw, History, ShieldCheck, Send } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { logError } from "@/lib/logger";
import { pdfUrl } from "@/lib/files";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const SECTIONS = {
  solicitant: ["name", "relationship", "birth_date", "birth_place", "fiscal_code", "id_doc", "id_number", "phone", "email", "address"],
  deceased: ["name", "citizenship", "birth_date", "birth_place", "death_date", "death_place", "fiscal_code", "last_address"],
  pickup: ["place_type", "contact", "contact_phone", "address"],
  destination: ["country", "county", "city", "cemetery", "chapel", "other", "address"],
};
const PLACE_TYPES = ["hospital", "morgue", "home", "care_home", "other"];
const REQUIRED = new Set([
  "solicitant.name", "solicitant.fiscal_code", "solicitant.phone", "solicitant.email",
  "solicitant.address", "solicitant.relationship", "deceased.name", "deceased.death_date",
  "deceased.death_place", "deceased.citizenship", "pickup.place_type", "pickup.address",
  "destination.country", "destination.city", "destination.address",
]);
const LANGS = ["it", "ro", "en", "fr", "de"];

const STATUS_STYLE = {
  draft: "bg-slate-100 text-slate-600", ready: "bg-amber-50 text-amber-700",
  signed: "bg-emerald-50 text-emerald-700", outdated: "bg-rose-50 text-rose-700",
};

export function MandateTab({ order, onUpdated }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const isStaff = ["admin", "support"].includes(user?.role);
  const [m, setM] = useState(null);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({});
  const [saving, setSaving] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [versions, setVersions] = useState([]);
  const [signer, setSigner] = useState(user?.name || "");
  const [gdpr, setGdpr] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [signing, setSigning] = useState(false);
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const hasDrawn = useRef(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get(`/orders/${order.id}/mandate`);
      setM(res);
      if (res.exists) setData(res.data || {});
    } catch (e) {
      if (e.response?.status === 403) setM({ exists: false, denied: true });
      else toast.error(formatApiError(e.response?.data?.detail));
    }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [order.id]);

  const setField = (sec, f, v) => setData((d) => ({ ...d, [sec]: { ...(d[sec] || {}), [f]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      const { data: res } = await api.put(`/orders/${order.id}/mandate`, { data });
      setM(res); setData(res.data || {}); toast.success(t("mand.saved"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const changeLang = async (lang) => {
    try {
      const { data: res } = await api.post(`/orders/${order.id}/mandate/language`, { language: lang });
      setM(res); setData(res.data || {});
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const regenerate = async () => {
    try {
      const { data: res } = await api.post(`/orders/${order.id}/mandate/regenerate`);
      setM(res); setData(res.data || {}); toast.success(t("mand.saved"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const loadVersions = async () => {
    setShowVersions((s) => !s);
    if (!showVersions) {
      try { const { data: v } = await api.get(`/orders/${order.id}/mandate/versions`); setVersions(v); }
      catch (e) { logError("mandate:versions", e); }
    }
  };

  const requestOtp = async () => {
    try {
      const { data: res } = await api.post(`/orders/${order.id}/mandate/request-otp`);
      setOtpSent(true);
      toast.success(res.status === "sent" ? t("mand.otpSent") : t("toast.emailMocked"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remind = async () => {
    try {
      const { data: res } = await api.post(`/orders/${order.id}/mandate/remind`);
      toast.success(`${t("mand.reminderSent")} (${res.count})`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // signature canvas
  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: cx - r.left, y: cy - r.top };
  };
  const startDraw = (e) => { drawing.current = true; const c = canvasRef.current.getContext("2d"); const p = pos(e); c.beginPath(); c.moveTo(p.x, p.y); };
  const moveDraw = (e) => { if (!drawing.current) return; e.preventDefault(); const c = canvasRef.current.getContext("2d"); const p = pos(e); c.lineWidth = 2; c.lineCap = "round"; c.strokeStyle = "#0f172a"; c.lineTo(p.x, p.y); c.stroke(); hasDrawn.current = true; };
  const endDraw = () => { drawing.current = false; };
  const clearCanvas = () => { const c = canvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); hasDrawn.current = false; };

  const sign = async () => {
    if (!gdpr) { toast.error(t("mand.gdprRequired")); return; }
    if (!signer.trim()) { toast.error(t("mand.signerName")); return; }
    setSigning(true);
    try {
      const img = hasDrawn.current ? canvasRef.current.toDataURL("image/png") : "";
      const { data: res } = await api.post(`/orders/${order.id}/mandate/sign`, {
        signer_name: signer.trim(), signature_image: img,
        signature_type: hasDrawn.current ? "drawn" : "typed", gdpr_consent: true, otp: otp.trim(),
      });
      setM(res); toast.success(t("mand.signed"));
      const { data: fresh } = await api.get(`/orders/${order.id}`); onUpdated?.(fresh);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSigning(false); }
  };

  if (loading) return <div className="text-slate-400 py-10" data-testid="mandate-loading">…</div>;
  if (!m?.exists) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-10 text-center text-slate-400" data-testid="mandate-unavailable">
        {t("mand.notAvailable")}
      </div>
    );
  }

  const signed = m.is_signed;
  const editable = m.can_edit && !signed;
  const fieldLabel = (f) => t(`mand.f.${f}`);
  const missingLabels = (m.missing || []).map((p) => fieldLabel(p.split(".")[1] || p));

  return (
    <div className="space-y-5" data-testid="mandate-panel">
      {/* Header */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="font-display text-lg font-semibold flex items-center gap-2"><FileSignature size={18} /> {t("mand.title")}</h3>
            <p className="text-sm text-slate-500 mt-0.5">{t("mand.sub")}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${STATUS_STYLE[m.status] || STATUS_STYLE.draft}`} data-testid="mandate-status">
              {t(`mand.st.${m.status}`)}
            </span>
            <Select value={m.language} onValueChange={changeLang} disabled={!m.can_edit && !isStaff}>
              <SelectTrigger className="h-9 w-[130px]" data-testid="mandate-language"><SelectValue /></SelectTrigger>
              <SelectContent>{LANGS.map((l) => <SelectItem key={l} value={l}>{l.toUpperCase()}</SelectItem>)}</SelectContent>
            </Select>
            <a href={pdfUrl(`orders/${order.id}/mandate/pdf`)} target="_blank" rel="noreferrer">
              <Button size="sm" variant="outline" data-testid="mandate-download"><Download size={14} className="mr-1.5" /> {t("mand.download")}</Button>
            </a>
            {isStaff && (signed || m.status === "outdated") && <Button size="sm" variant="outline" onClick={regenerate} data-testid="mandate-regenerate"><RefreshCw size={14} className="mr-1.5" /> {t("mand.regenerate")}</Button>}
            {isStaff && !signed && <Button size="sm" onClick={remind} className="bg-rose-600 hover:bg-rose-700" data-testid="mandate-remind"><Send size={14} className="mr-1.5" /> {t("mand.remind")}</Button>}
            <Button size="sm" variant="ghost" onClick={loadVersions} data-testid="mandate-versions-toggle"><History size={14} className="mr-1.5" /> {t("mand.versions")}</Button>
          </div>
        </div>

        {/* completion */}
        <div className="mt-4">
          <div className="flex justify-between text-xs text-slate-500 mb-1">
            <span>{t("mand.progress")}</span><span data-testid="mandate-completion">{m.completion_pct}%</span>
          </div>
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
            <div className={`h-full rounded-full transition-all ${m.completion_pct === 100 ? "bg-emerald-500" : "bg-amber-400"}`} style={{ width: `${m.completion_pct}%` }} />
          </div>
          {m.completion_pct < 100 && (
            <p className="text-xs text-amber-600 mt-2" data-testid="mandate-missing">{t("mand.missing")}: {missingLabels.join(", ")}</p>
          )}
        </div>

        {showVersions && (
          <div className="mt-4 border-t border-slate-100 pt-3 space-y-1.5" data-testid="mandate-versions">
            {versions.map((v) => (
              <div key={v.id} className="flex items-center justify-between text-xs">
                <span>{t("mand.version")} {v.version} · {t(`mand.st.${v.status}`)} · {(v.language || "").toUpperCase()}</span>
                <a href={pdfUrl(`orders/${order.id}/mandate/pdf?version=${v.version}`)} target="_blank" rel="noreferrer" className="text-blue-700">PDF</a>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Editable form */}
      {Object.entries(SECTIONS).map(([sec, fields]) => (
        <div key={sec} className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <h4 className="font-display font-semibold mb-3">{t(`mand.s.${sec}`)}</h4>
          <div className="grid sm:grid-cols-2 gap-3">
            {fields.map((f) => {
              const req = REQUIRED.has(`${sec}.${f}`);
              const val = (data[sec] || {})[f] || "";
              return (
                <div key={f} className="space-y-1.5">
                  <Label className="text-xs">{fieldLabel(f)}{req && <span className="text-rose-500"> *</span>}</Label>
                  {f === "place_type" ? (
                    <Select value={val} onValueChange={(v) => setField(sec, f, v)} disabled={!editable}>
                      <SelectTrigger data-testid={`mandate-${sec}-${f}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{PLACE_TYPES.map((p) => <SelectItem key={p} value={p}>{t(`mand.pt.${p}`)}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : (
                    <Input value={val} onChange={(e) => setField(sec, f, e.target.value)} disabled={!editable} data-testid={`mandate-${sec}-${f}`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {editable && (
        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} className="bg-blue-900 hover:bg-blue-800" data-testid="mandate-save">{saving ? "…" : t("mand.save")}</Button>
        </div>
      )}

      {/* Signature block */}
      {signed ? (
        <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-5" data-testid="mandate-signed-box">
          <div className="flex items-center gap-2 font-semibold text-emerald-800"><ShieldCheck size={18} /> {t("mand.signed")}</div>
          <div className="grid sm:grid-cols-2 gap-2 mt-3 text-sm text-slate-700">
            <div><span className="text-slate-500">{t("mand.signedBy")}:</span> {m.signature?.signer_name}</div>
            <div><span className="text-slate-500">{t("mand.signedAt")}:</span> {(m.signature?.timestamp || "").replace("T", " ").slice(0, 19)}</div>
            <div><span className="text-slate-500">IP:</span> {m.signature?.ip}</div>
            <div><span className="text-slate-500">{t("mand.otpVerified")}:</span> {m.signature?.otp_verified ? "✓" : "—"}</div>
          </div>
          {m.document_hash && <div className="text-[11px] text-slate-400 mt-2 break-all">{t("mand.hash")}: {m.document_hash}</div>}
        </div>
      ) : m.can_sign ? (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5" data-testid="mandate-sign-box">
          <h4 className="font-display font-semibold flex items-center gap-2"><FileSignature size={18} /> {t("mand.sign")}</h4>
          <p className="text-xs text-slate-500 mt-1">{t("mand.signHint")}</p>
          {m.completion_pct < 100 && <p className="text-sm text-amber-600 mt-2">{t("mand.missing")}: {missingLabels.join(", ")}</p>}
          {m.completion_pct === 100 && (
            <div className="mt-4 space-y-3">
              <div className="space-y-1.5"><Label className="text-xs">{t("mand.signerName")}</Label>
                <Input value={signer} onChange={(e) => setSigner(e.target.value)} data-testid="mandate-signer-name" /></div>
              <div className="space-y-1.5"><Label className="text-xs">{t("mand.draw")}</Label>
                <canvas ref={canvasRef} width={480} height={140}
                  className="w-full max-w-md border border-slate-300 rounded-lg touch-none bg-slate-50"
                  onMouseDown={startDraw} onMouseMove={moveDraw} onMouseUp={endDraw} onMouseLeave={endDraw}
                  onTouchStart={startDraw} onTouchMove={moveDraw} onTouchEnd={endDraw}
                  data-testid="mandate-signature-canvas" />
                <Button size="sm" variant="ghost" onClick={clearCanvas} data-testid="mandate-clear-signature">{t("mand.clear")}</Button>
              </div>
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <Checkbox checked={gdpr} onCheckedChange={setGdpr} data-testid="mandate-gdpr" className="mt-0.5" />
                <span>{t("mand.gdpr")}</span>
              </label>
              {m.otp_needed && (
                <div className="flex items-end gap-2 flex-wrap">
                  <Button size="sm" variant="outline" onClick={requestOtp} data-testid="mandate-request-otp"><Send size={14} className="mr-1.5" /> {t("mand.requestOtp")}</Button>
                  {otpSent && <div className="space-y-1"><Label className="text-xs">{t("mand.otpCode")}</Label>
                    <Input value={otp} onChange={(e) => setOtp(e.target.value)} className="w-36" data-testid="mandate-otp-input" /></div>}
                </div>
              )}
              <Button onClick={sign} disabled={signing} className="bg-emerald-600 hover:bg-emerald-700" data-testid="mandate-sign-btn">{signing ? "…" : t("mand.signNow")}</Button>
            </div>
          )}
        </div>
      ) : (
        <p className="text-sm text-slate-400" data-testid="mandate-readonly">{t("mand.readonly")}</p>
      )}
    </div>
  );
}
